/**
 * GatheringStormSkillEffect — passive that provides a conditional damage/area
 * buff when chain strikes (Chain Lightning) hit enough targets.
 *
 * The trigger lives in ChainLightningSkillEffect: after each cast, if total
 * targets hit >= chainThreshold, it calls applyPlayerStatus('gathering_storm_buff').
 * This passive owns the consumption interface — other Lightning skills call
 * hasBuff() before activation, apply getBonusDamage() / getBonusArea() to their
 * output, then consumeBuff() to clear the charge.
 *
 * Single-charge toggle: the buff either exists or it doesn't. Multiple
 * consecutive qualifying casts do not stack.
 *
 * All values are data-driven from lightning.json via the effect block on the
 * arc_path_passive node. Downstream passives can modify bonusDamage / bonusArea
 * through UpgradeEffectResolver tree-scope aggregation.
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

export const GATHERING_STORM_BUFF_ID = 'gathering_storm_buff';

// ---------------------------------------------------------------------------
// Dependencies
// ---------------------------------------------------------------------------

export interface GatheringStormDeps {
  /** Check whether a player-side status is currently active. */
  readonly hasPlayerStatus: (statusId: string) => boolean;
  /** Remove a player-side status (consume the buff). */
  readonly removePlayerStatus: (statusId: string) => void;
  readonly resolver: {
    getTreePassiveEffect(tree: string, key: string, aggregation: string): number;
  };
}

// ---------------------------------------------------------------------------
// Config (populated from lightning.json effect block)
// ---------------------------------------------------------------------------

export interface GatheringStormConfig {
  /** Minimum chain hit count to trigger the buff. */
  readonly chainThreshold: number;
  /** Bonus damage multiplier applied to the next Lightning skill. */
  readonly bonusDamage: number;
  /** Bonus area multiplier (radius scale) applied to the next Lightning skill. */
  readonly bonusArea: number;
}

// ---------------------------------------------------------------------------
// Defaults (mirror the JSON values — used when no config override is provided)
// ---------------------------------------------------------------------------

const DEFAULT_CONFIG: GatheringStormConfig = {
  chainThreshold: 3,
  bonusDamage: 0.25,
  bonusArea: 0.2,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class GatheringStormSkillEffect implements SkillEffect {
  readonly skillId = 'arc_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: GatheringStormDeps;
  private readonly config: GatheringStormConfig;

  constructor(deps: GatheringStormDeps, config?: Partial<GatheringStormConfig>) {
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /** Whether the gathering storm buff is currently active on the player. */
  hasBuff(): boolean {
    return this.deps.hasPlayerStatus(GATHERING_STORM_BUFF_ID);
  }

  /**
   * Consume the buff. Returns true if it was active (and is now cleared).
   * Returns false if no buff was present.
   */
  consumeBuff(): boolean {
    if (!this.deps.hasPlayerStatus(GATHERING_STORM_BUFF_ID)) return false;
    this.deps.removePlayerStatus(GATHERING_STORM_BUFF_ID);
    return true;
  }

  /**
   * Resolve bonus damage multiplier including any tree-scope modifiers
   * from downstream passives.
   */
  getBonusDamage(): number {
    const additiveBonus = this.deps.resolver.getTreePassiveEffect(
      'lightning', 'bonusDamage', 'sum',
    );
    return this.config.bonusDamage + additiveBonus;
  }

  /**
   * Resolve bonus area multiplier including any tree-scope modifiers
   * from downstream passives.
   */
  getBonusArea(): number {
    const additiveBonus = this.deps.resolver.getTreePassiveEffect(
      'lightning', 'bonusArea', 'sum',
    );
    return this.config.bonusArea + additiveBonus;
  }

  /** No per-frame work — buff state is tracked by the StatusEffectSystem. */
  update(_delta: number): void {}

  destroy(): void {}
}
